import { DynamicForm } from "@shared/components/DynamicForm";
import { StepHelpers } from "@shared/components/Stepper";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAssetDetail, useDimensionList, useUpdateAsset } from "../../hooks";

interface AssetCharacteristic {
  name: string;
  unit?: string;
  dataType: string;
  isActive: boolean;
  createdBy: string;
  isRequired: boolean;
  createdDate: string;
  optionValue: string[];
  assetTypeCharacteristics: string;
  code: string;
  hierarchyNames?: {
    assetClass: string;
    assetType: string;
    assetTypeCharacteristics: string;
  };
  hierarchyCodes?: {
    assetClass: string;
    assetType: string;
    assetTypeCharacteristics: string;
  };
  totalChildCount?: number;
  failureModeCount?: number;
  dimensionsCount?: number;
  childCount: number;
  entityTypeDisplay: string;
}

interface Component {
  id: string;
  name: string;
}

interface Step2SpecificInfoProps {
  editMode: boolean;
  // Props automatically passed by Stepper
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  data?: any;
  onDataChange?: (data: any) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
  allStepData?: Record<number, any>;
}

const Step2SpecificInfo: React.FC<Step2SpecificInfoProps> = ({
  editMode,
  stepHelpers,
  currentStepIndex = 1,
  data = {},
  onDataChange,
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  setValidationError,
  clearValidationError,
  isValidating,
  allStepData,
}) => {
  const { toast } = useToast();
  const { id, action } = useParams();
  const dataLoadedRef = useRef(false);

  const { mutateAsync: updateAsset, isPending: isUpdating } =
    useUpdateAsset(id);

  const { data: assetDetail } = useAssetDetail(id);

  const saveStepData = useCallback(
    (stepData: any) => {
      if (!stepHelpers) return;

      try {
        const dataToSave = {
          ...stepData,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, dataToSave);
        console.log("Step 2 data saved:", dataToSave);
      } catch (error) {
        console.error("Failed to save step 2 data:", error);
      }
    },
    [stepHelpers, currentStepIndex]
  );

  const getStepData = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(currentStepIndex) || {};
    } catch (error) {
      console.error("Failed to get step 2 data:", error);
      return {};
    }
  }, [stepHelpers, currentStepIndex]);

  const getStep1Data = useCallback(() => {
    if (!stepHelpers) return {};

    try {
      return stepHelpers.getStepData(0) || {}; // Step 1 is at index 0
    } catch (error) {
      console.error("Failed to get step 1 data:", error);
      return {};
    }
  }, [stepHelpers]);

  // Load saved data on component mount
  useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        const savedStepData = getStepData();
        const step1Data = getStep1Data();
        if (savedStepData && Object.keys(savedStepData).length > 0) {
          onDataChange?.(savedStepData);
        }

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step 2 data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData, getStep1Data, onDataChange]);

  const { data: dimensionFields } = useDimensionList(
    assetDetail?.assetTypeCode
  );

  // Function to convert data_type to form field type
  const getFormFieldType = (dataType: string): FormField["type"] => {
    switch (dataType?.toLowerCase()) {
      case "number":
        return "number";
      case "dropdown":
        return "select";
      case "boolean":
        return "select";
      case "text":
      case "string":
        return "text";
      case "date":
        return "datepicker";
      default:
        return "text";
    }
  };

  // Function to get options for select fields
  const getFieldOptions = (characteristic: AssetCharacteristic) => {
    if (characteristic.dataType?.toLowerCase() === "boolean") {
      return [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ];
    }

    if (
      characteristic.dataType?.toLowerCase() === "dropdown" &&
      characteristic.optionValue.length > 0
    ) {
      return characteristic.optionValue.map((option) => ({
        value: option,
        label: option,
      }));
    }

    return undefined;
  };

  // Function to get default value based on data type
  const getDefaultValue = (characteristic: AssetCharacteristic) => {
    const existingValue = data.specificFields?.[characteristic.code];
    if (existingValue !== undefined) {
      return existingValue;
    }

    switch (characteristic.dataType?.toLowerCase()) {
      case "number":
        return 0;
      case "boolean":
        return "no";
      case "dropdown":
        return "";
      default:
        return "";
    }
  };

  // Create form fields from asset characteristics
  const formFields: FormField[] = useMemo(() => {
    if (
      !dimensionFields ||
      !Array.isArray(dimensionFields) ||
      dimensionFields.length === 0
    ) {
      return [];
    }

    return dimensionFields.map((dimension) => {
      const label = dimension.unit
        ? `${dimension.name} (${dimension.unit})`
        : dimension.name;

      return {
        name: dimension.name,
        label,
        type: getFormFieldType(dimension.dataType),
        required: dimension.isRequired,
        placeholder: `Enter ${dimension.name?.toLowerCase()}`,
        options: getFieldOptions(dimension),
        defaultValue: getDefaultValue(dimension),
        min: dimension.dataType?.toLowerCase() === "number" ? 0 : undefined,
        step: dimension.dataType?.toLowerCase() === "number" ? 0.01 : undefined,
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
      };
    });
  }, [dimensionFields, assetDetail]);

  const handleNext = async () => {
    // Clear any previous validation errors
    if (clearValidationError) {
      clearValidationError();
    }

    try {
      const isValid = await FormService.validateForm(form);
      if (!isValid) {
        if (setValidationError) {
          setValidationError("Please fill in all required fields.");
        }
        toast({
          title: "Please fill in all required fields.",
          variant: "destructive",
        });
        return;
      }

      const step1Data = getStep1Data();
      const formData = form.getValues();

      const filteredFormData = Object.fromEntries(
        Object.entries(formData).filter(
          ([, value]) =>
            value !== "" && value !== 0 && value !== null && value !== undefined
        )
      );

      const payload = { dimensions: filteredFormData };

      const response = await updateAsset(payload);

      if (response) {
        // Save completion status
        const completedData = {
          ...data,
          specificFields: data.specificFields || {},

          payload,
          response,
          id, // Pass along the ID for next steps
          isStepComplete: true,
          completedAt: new Date().toISOString(),
        };

        onDataChange?.(completedData);
        saveStepData(completedData);
        onNext?.();
      } else {
        if (setValidationError) {
          setValidationError("Failed to update asset data. Please try again.");
        }
      }
    } catch (error) {
      console.error("Failed to continue from step 2:", error);
      if (setValidationError) {
        setValidationError(
          "An error occurred while processing. Please try again."
        );
      }
    }
  };

  const handlePrevious = () => {
    // Save current state before going back
    const currentData = {
      ...data,

      specificFields: data.specificFields || {},
      navigatedBack: true,
      navigatedBackAt: new Date().toISOString(),
    };

    onDataChange?.(currentData);
    saveStepData(currentData);
    onPrevious?.();
  };

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  useEffect(() => {
    if (assetDetail?.dimensions) {
      FormService.populateForm(form, assetDetail?.dimensions, formFields);
    }
  }, [assetDetail, formFields]);

  return (
    <div className="bg-white p-4">
      <div className="mb-3">
        <p className="page-title">Asset-Specific Information</p>
        <p className="page-subtitle mt-2">
          Configure technical specifications and manage components for your
          selected asset.
        </p>
      </div>

      <div className="space-y-8">
        {/* Asset-Specific Fields */}
        <div>
          <h3 className="text-lg font-medium mb-4">Asset Parameters</h3>
          {formFields.length > 0 ? (
            <DynamicForm
              fields={formFields}
              form={form}
              // onFieldChange={handleFieldChange}
              config={{
                layout: "vertical",
                showSubmitButton: false,
                showResetButton: false,
                spacing: "medium",
              }}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">
              No asset dimensions found for this asset type.
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
        <Button onClick={handlePrevious} variant="outline">
          Previous
        </Button>
        <Button onClick={handleNext} isLoading={isUpdating}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step2SpecificInfo;
